import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, Typography } from "@mui/material";

import { OnboardingStepLayout, EmailInput } from "./../../../shared/ui/index";
import { Step6Schema, type Step6Input } from "../../../shared/schemas/step6";
import { useTranslation } from "react-i18next";

export type Step6FormValues = Step6Input;

type Step6Props = {
  currentStep: number;
  totalSteps: number;
  initialValues?: Step6FormValues;
  onCompleted: (data: Step6FormValues) => void;
  onBack: () => void;
};

export default function Step6_CompanyVerification({
  currentStep,
  totalSteps,
  initialValues,
  onCompleted,
  onBack,
}: Step6Props) {
  const methods = useForm<Step6FormValues>({
    resolver: zodResolver(Step6Schema),
    defaultValues: initialValues ?? {
      verificationEmail: "",
    },
  });

  const { handleSubmit } = methods;
  const { t } = useTranslation();

  const onSubmit = handleSubmit((data) => {
    sessionStorage.setItem("step6", JSON.stringify(data));
    onCompleted(data);
  });

  return (
    <FormProvider {...methods}>
      <OnboardingStepLayout
        step={currentStep}
        totalSteps={totalSteps}
        title={t("onboarding.step6.title")}
        subtitle={t("onboarding.step6.subtitle")}
        onNext={onSubmit}
        nextLabel={t("onboarding.step6.actions.next")}
        onBack={onBack}
        backLabel={t("onboarding.step6.actions.back")}
      >
        <Box sx={{ maxWidth: 640 }}>
          <Stack spacing={4}>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "#4A5568",
                  mb: 1,
                }}
              >
                {t("onboarding.step6.info.intro")}
              </Typography>

              <Typography
                component="ul"
                sx={{
                  pl: 3,
                  m: 0,
                  fontSize: "0.9rem",
                  color: "#4A5568",
                  "& li": { mb: 0.5 },
                }}
              >
                <li>{t("onboarding.step6.info.items.idDocument")}</li>
                <li>{t("onboarding.step6.info.items.incorporationDocs")}</li>
                <li>{t("onboarding.step6.info.items.bankStatement")}</li>
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#1A1A1A",
                  mb: 1,
                }}
              >
                {t("onboarding.step6.emailSection.label")}
                <Box component="span" sx={{ color: "#E63946", ml: 0.5 }}>
                  *
                </Box>
              </Typography>

              <EmailInput<Step6FormValues, "verificationEmail">
                name="verificationEmail"
                label=""
                placeholder={t("onboarding.step6.emailSection.placeholder")}
              />
            </Box>
          </Stack>
        </Box>
      </OnboardingStepLayout>
    </FormProvider>
  );
}
